import { yupResolver } from "@hookform/resolvers/yup";
import type { FlagProps } from "@wayflyer/flyui";
import { Flag, useToast } from "@wayflyer/flyui";
import { useRhfAnalytics } from "@wayflyer/flyui-hook-form";
import type { BankConnectCountryConfig } from "@wayflyer/customer-bankconnect";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { usePostHog } from "posthog-js/react";
import type { BankConnectCountryConfig as BankButtonBankConnectCountryConfig } from "shared-ui";
import { handleContactTeam } from "shared-utils";
import * as Yup from "yup";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { MESSAGES } from "../../../components/bank-controller-next/message-definitions";

type UseCountrySelectionFormProps = {
  bankConnectCountries: BankConnectCountryConfig[];
  countryOfIncorporation: string | null | undefined;
  requireConnection: boolean;
  startConnect: (selection: BankButtonBankConnectCountryConfig) => Promise<void>;
};

export const useCountrySelectionForm = ({
  bankConnectCountries,
  countryOfIncorporation,
  requireConnection,
  startConnect,
}: UseCountrySelectionFormProps) => {
  const intl = useIntl();
  const posthog = usePostHog();
  const toaster = useToast();

  // Omit confirmEmail-only countries (e.g. BE, DK) since we display NOT_LISTED as an option.
  const integrationCountries = bankConnectCountries.filter((c) => !(c.confirmEmail && c.code !== "NOT_LISTED"));

  const defaultCountry = integrationCountries.find((c) => c.code === countryOfIncorporation);

  const countryOptions = integrationCountries.map((item) => ({
    ...item,
    text: item.name,
    id: item.code,
    icon: item.code !== "NOT_LISTED" ? <Flag code={item.code as FlagProps["code"]} /> : undefined,
  }));

  const countrySchema = Yup.object().shape({
    country: Yup.string().trim().required(intl.formatMessage(MESSAGES.selectCountryError)),
  });

  const countryForm = useForm<{ country: string }>({
    resolver: yupResolver(countrySchema),
    defaultValues: { country: defaultCountry?.code ?? "" },
  });

  useRhfAnalytics({
    form: countryForm,
    captureFn: (eventName, properties) => posthog?.capture(eventName, properties),
    formName: "onboarding_banking_connection",
    fieldsToTrack: ["country"],
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const selectedCountryCode = countryForm.watch("country");
  const selectedCountry = integrationCountries.find((c) => c.code === selectedCountryCode);
  const providerName = selectedCountry?.providerName ?? intl.formatMessage(COMMON_MESSAGES.connected);
  const isConnectionUnsupportedCountry = selectedCountry?.confirmEmail ?? false;
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    if (isConnectionUnsupportedCountry && toaster) {
      if (requireConnection) {
        // Statement upload is disabled when a connection is required, so point to support instead.
        toaster.info(intl.formatMessage(MESSAGES.countryNotListedRequireConnectionToast), {
          action: {
            content: intl.formatMessage(MESSAGES.contactSupportAction),
            onAction: handleContactTeam,
          },
        });
      } else {
        toaster.info(intl.formatMessage(MESSAGES.countryNotListedToast));
      }
    }
  }, [isConnectionUnsupportedCountry, requireConnection, toaster, intl]);

  const handleConnect = async (): Promise<boolean> => {
    let started = false;
    await countryForm.handleSubmit(async (data) => {
      const selection = integrationCountries.find((c) => c.code === data.country);
      if (!selection) {
        return;
      }
      posthog?.capture("onboarding_bank_connection_started", {
        provider: selection.providerName,
        provider_type: "banking",
        country_code: selection.code,
      });
      setIsConnecting(true);
      try {
        await startConnect(selection as BankButtonBankConnectCountryConfig);
        started = true;
      } finally {
        setIsConnecting(false);
      }
    })();
    return started;
  };

  return {
    countryForm,
    countryOptions,
    defaultCountry,
    providerName,
    isConnectionUnsupportedCountry,
    isConnecting,
    handleConnect,
  };
};
